// src/components/Sidebar.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PageId } from "../types/page";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import { MALL_LABELS, type Mall } from "../types/mall";
import { logout } from "../utils/firebaseAuth";
import { getGuideUrl } from "./SetupBanners";

interface SidebarProps {
    page: PageId;
    setPage: (page: PageId) => void;
    sidebarOpen: boolean; // 互換のため残す（使わない）
    setSidebarOpen: (open: boolean) => void;
}

type SectionKey =
    | "goal"
    | "profit"
    | "analysis"
    | "cross"
    | "settings"
    | "premium"
    | "support";

function cx(...classes: Array<string | false | null | undefined>) {
    return classes.filter(Boolean).join(" ");
}

const OPEN_SECTIONS_LS_KEY = "sidebarOpenSections";

const OPEN_SECTIONS_DEFAULT: Record<SectionKey, boolean> = {
    goal: true,
    profit: true,
    analysis: false,
    cross: false,
    settings: false,
    premium: false,
    support: false,
};

function loadOpenSections(): Record<SectionKey, boolean> {
    try {
        const raw = localStorage.getItem(OPEN_SECTIONS_LS_KEY);
        if (!raw) return OPEN_SECTIONS_DEFAULT;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return OPEN_SECTIONS_DEFAULT;
        return { ...OPEN_SECTIONS_DEFAULT, ...parsed };
    } catch {
        return OPEN_SECTIONS_DEFAULT;
    }
}

function lsGet(key: string) {
    if (typeof window === "undefined") return "";
    return String(localStorage.getItem(key) || "").trim();
}

const PREMIUM_RGB = "rgb(186, 117, 23)";

function ChevronIcon({ closed }: { closed: boolean }) {
    return (
        <svg
            viewBox="0 0 14 14"
            fill="none"
            className={cx(
                "h-[14px] w-[14px] shrink-0 text-[var(--nav-text-muted)] transition-transform duration-200 ease-out",
                closed && "-rotate-90"
            )}
        >
            <path
                d="M3 5l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SectionGoalIcon() {
    // 目標を立てる：的（ターゲット）の中心を射抜くイメージ
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
            <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.2" opacity="0.6" />
            <circle cx="8" cy="8" r="1.3" fill="currentColor" />
        </svg>
    );
}

function SectionProfitIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <rect x="1" y="9" width="3" height="6" rx="1" fill="currentColor" opacity="0.5" />
            <rect x="6" y="6" width="3" height="9" rx="1" fill="currentColor" opacity="0.7" />
            <rect x="11" y="3" width="3" height="12" rx="1" fill="currentColor" />
            <path d="M2 6L6 4L10 5.5L14 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function SectionAnalysisIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <circle cx="6.5" cy="6.5" r="4.5" stroke="currentColor" strokeWidth="1.3" />
            <path d="M10 10l4 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            <path d="M5 6.5h3M6.5 5v3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function SectionSettingsIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.3" />
            <path
                d="M8 1v2M8 13v2M1 8h2M13 8h2M3.2 3.2l1.4 1.4M11.4 11.4l1.4 1.4M3.2 12.8l1.4-1.4M11.4 4.6l1.4-1.4"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
        </svg>
    );
}

function SectionPremiumIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <path
                d="M8 1l1.8 3.6L14 5.5l-3 2.9.7 4.1L8 10.5l-3.7 2 .7-4.1L2 5.5l4.2-.9L8 1z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function SectionSupportIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3" />
            <path
                d="M6 6c0-1.1.9-2 2-2s2 .9 2 2c0 1.4-2 2-2 2v1"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
            <circle cx="8" cy="12" r="0.8" fill="currentColor" />
        </svg>
    );
}

function IconDashboard() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="1" width="6" height="6" rx="1" fill="currentColor" opacity="0.7" />
            <rect x="9" y="1" width="6" height="6" rx="1" fill="currentColor" opacity="0.4" />
            <rect x="1" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.4" />
            <rect x="9" y="9" width="6" height="6" rx="1" fill="currentColor" opacity="0.7" />
        </svg>
    );
}

function IconGoalFlag() {
    // 目標設定：ゴール旗。親（的＝SectionGoalIcon）と重複しないよう旗で表す
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M4 1.5v13" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path
                d="M4 2.5h7.5l-1.6 2.5 1.6 2.5H4"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconMonthly() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 2v12M1 7h14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
        </svg>
    );
}

function IconDaily() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M8 4.5v3.5l2 2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function IconPeriod() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 2v3M11 2v3M1 7h14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
            <path d="M4 10h4M4 12.5h7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>
    );
}

function IconSingleProfit() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M2 3h12a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1V4a1 1 0 011-1z"
                stroke="currentColor"
                strokeWidth="1.2"
            />
            <path
                d="M2 9h8a1 1 0 011 1v2a1 1 0 01-1 1H2a1 1 0 01-1-1v-2a1 1 0 011-1z"
                stroke="currentColor"
                strokeWidth="1.2"
            />
        </svg>
    );
}

function IconSummaryProfit() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M1 5h14M1 9h14" stroke="currentColor" strokeWidth="1" opacity="0.5" />
            <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M6 2v12M11 2v12" stroke="currentColor" strokeWidth="1" opacity="0.4" />
        </svg>
    );
}

function IconPromotion() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M2 13L5 9l3 2 3-4 3-3"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconAds() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="4" width="14" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 4V3a1 1 0 011-1h4a1 1 0 011 1v1" stroke="currentColor" strokeWidth="1.2" />
            <path d="M4 8h8M4 11h5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
        </svg>
    );
}

function IconRepeat() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M8 2C5 2 2.5 4.5 2.5 7c0 1.5.7 2.8 1.8 3.7L3.5 14l3-1.5c.5.1 1 .2 1.5.2 3 0 5.5-2.5 5.5-5.5S11 2 8 2z"
                stroke="currentColor"
                strokeWidth="1.2"
            />
        </svg>
    );
}

function IconAI() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M8 2a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" fill="currentColor" opacity="0.6" />
            <path
                d="M3 8.5c0-2.8 2.2-5 5-5s5 2.2 5 5"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
            <path
                d="M5 10l1.5 2L8 9.5l1.5 3L11 10"
                stroke="currentColor"
                strokeWidth="1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconCategory() {
    // カテゴリー：カテゴリ記号（4つの図形でカテゴリ分けを表す）
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1.6" y="1.6" width="5.4" height="5.4" rx="1" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="11.7" cy="4.3" r="2.7" stroke="currentColor" strokeWidth="1.2" />
            <path d="M4.3 9.1L7 14.4H1.6z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            <path d="M11.7 8.9L14.5 11.7L11.7 14.5L8.9 11.7z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
        </svg>
    );
}

function IconProductName() {
    // 商品名：複数タグ（キーワードで横断・比較する）
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M7 2.5h1.3a1 1 0 01.71.29l4.5 4.5a1 1 0 010 1.42l-1.1 1.1"
                stroke="currentColor"
                strokeWidth="1.1"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
            />
            <path
                d="M2.4 7.3V3.5a1 1 0 011-1H7a1 1 0 01.71.3l4.5 4.5a1 1 0 010 1.41l-3.3 3.3a1 1 0 01-1.41 0L2.7 7.7a1 1 0 01-.3-.4z"
                stroke="currentColor"
                strokeWidth="1.1"
                strokeLinejoin="round"
            />
            <circle cx="4.9" cy="5" r="0.9" fill="currentColor" />
        </svg>
    );
}

function IconProductAnalysis() {
    // 商品分析：個別商品（ミクロ）を表すパッケージ
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M8 1.5L14 4.5v7L8 14.5 2 11.5v-7L8 1.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            <path
                d="M2 4.5L8 7.5l6-3M8 7.5v7"
                stroke="currentColor"
                strokeWidth="1.1"
                strokeLinejoin="round"
                opacity="0.55"
            />
        </svg>
    );
}

function IconInitialSettings() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="2" width="14" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path
                d="M5 7l2 2 4-4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconCost() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M8 1L3 4v4c0 3 2.2 5.8 5 6 2.8-.2 5-3 5-6V4L8 1z" stroke="currentColor" strokeWidth="1.2" />
            <path
                d="M5.5 8l2 2 3-3"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconFixedCost() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M5 6h6M5 9h4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
        </svg>
    );
}

function IconVariableCost() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="2" y="2" width="12" height="12" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M4 11l3-4 2 2 3-5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function IconPlanBilling() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M1.5 6h13" stroke="currentColor" strokeWidth="1" opacity="0.55" />
            <circle cx="11.5" cy="10" r="1.2" fill="currentColor" opacity="0.75" />
        </svg>
    );
}

function IconUsers() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.2" />
            <path
                d="M2.5 13.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
        </svg>
    );
}

function IconPastData() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M8 5v4M8 11v1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
    );
}

function IconReport() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M3 2h7l3 3v9H3V2z" stroke="currentColor" strokeWidth="1.2" />
            <path d="M10 2v3h3" stroke="currentColor" strokeWidth="1.2" />
        </svg>
    );
}

function IconConsult() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <circle cx="5" cy="5" r="3" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="11" cy="5" r="3" stroke="currentColor" strokeWidth="1.2" />
            <path
                d="M1.5 14c0-2 1.5-3.5 3.5-3.5h6c2 0 3.5 1.5 3.5 3.5"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
            />
        </svg>
    );
}

function IconCreative() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="3" width="10" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="6" cy="7" r="2" stroke="currentColor" strokeWidth="1" />
            <path
                d="M11 6l4-2v6l-4-2"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconContact() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="3" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
            <path d="M1 5l7 5 7-5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
        </svg>
    );
}

function IconAmazon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M2 10.5c3.5 2 8.5 2 12-1"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
            />
            <path
                d="M12 11.5l2-1.5-1 2.5"
                stroke="currentColor"
                strokeWidth="1.1"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M5 7.5c0-1.7 1.3-3 3-3s3 1.3 3 3c0 .8-.3 1.5-.8 2H5.8C5.3 9 5 8.3 5 7.5z"
                stroke="currentColor"
                strokeWidth="1.2"
            />
        </svg>
    );
}

function IconYahoo() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <text
                x="8"
                y="12"
                textAnchor="middle"
                fontSize="12"
                fontWeight="700"
                fill="currentColor"
            >
                Y!
            </text>
        </svg>
    );
}

function IconCrossDashboard() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
            <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
            <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
            <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.4" />
            <path d="M7 4h2M8 7v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
    );
}

function SectionCrossIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <rect x="1" y="1" width="6.5" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
            <rect x="8.5" y="1" width="6.5" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.2" />
            <rect x="1" y="8.5" width="6.5" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
            <rect x="8.5" y="8.5" width="6.5" height="6.5" rx="1" stroke="currentColor" strokeWidth="1.2" opacity="0.5" />
        </svg>
    );
}

function IconRequest() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path
                d="M8 1l1.5 3 3.5.5-2.5 2.5.5 3.5L8 9l-3 1.5.5-3.5L3 4.5 6.5 4z"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function IconGuide() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
            <path d="M3 2h10a1 1 0 011 1v8a1 1 0 01-1 1H5l-3 2V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.2" />
        </svg>
    );
}

function IconLogout() {
    return (
        <svg viewBox="0 0 16 16" fill="none" className="h-[15px] w-[15px]">
            <path d="M6 3H3a1 1 0 00-1 1v8a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path
                d="M11 5l3 3-3 3M14 8H6"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

const Sidebar: React.FC<SidebarProps> = ({
    page,
    setPage,
    sidebarOpen: _sidebarOpen,
    setSidebarOpen,
}) => {
    const user = useUserContext();
    const navigate = useNavigate();
    const location = useLocation();

    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [planForMenu, setPlanForMenu] = useState<string>(() => lsGet("plan"));

    const isAdmin = user.superAdmin || user.role === "admin";
    const basePrefix = location.pathname.startsWith("/demo") ? "/demo" : "/app";
    const isDemo = basePrefix === "/demo";

    const isFreePlan = useMemo(() => {
        const p = String(planForMenu || "").toLowerCase();
        return p.includes("free") || p.includes("フリー");
    }, [planForMenu]);

    useEffect(() => {
        const refreshPlanFromStorage = () => {
            const newPlan = lsGet("plan");
            console.log("[Sidebar] company-selection-changed: refreshPlanFromStorage", {
                newPlan,
                planForMenuBefore: planForMenu,
            });
            setPlanForMenu(newPlan);
        };

        refreshPlanFromStorage();

        if (typeof window !== "undefined") {
            window.addEventListener("company-selection-changed", refreshPlanFromStorage);
            return () => window.removeEventListener("company-selection-changed", refreshPlanFromStorage);
        }
    }, []);

    const pageToSection = useMemo<Partial<Record<PageId, SectionKey>>>(
        () => ({
            targetDashboard: "goal",

            dashboard: "profit",
            monthly: "profit",
            daily: "profit",
            periodSnapshots: "profit",
            itemProfitDetail: "profit",
            itemProfitSummary: "profit",
            shippingCostSettings: "profit",
            product: "profit",

            categoryAnalysis: "analysis",
            productNameAnalysis: "analysis",
            productAnalysis: "analysis",
            promotion: "analysis",
            ads: "analysis",
            ltv: "analysis",
            aiAnalysis: "analysis",


            settings: "settings",
            planBilling: "settings",
            itemCost: "settings",
            fiscalRakutenExpense: "settings",
            variableCostSettings: "settings",
            amazonSettings: "settings",
            yahooSettings: "settings",
            users: "settings",

            rmsManual: "premium",
            contact: "support",
        }),
        []
    );

    const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>(loadOpenSections);

    const { mall, setMall, hasAmazon, hasRakuten, hasYahoo, enabledMalls } = useMallContext();
    const mallOptions = useMemo<Mall[]>(() => ["rakuten", "amazon", "yahoo"], []);
    const availableMalls = useMemo(() => {
        const set = new Set<Mall>();
        if (hasRakuten) set.add("rakuten");
        if (hasAmazon) set.add("amazon");
        if (hasYahoo) set.add("yahoo");
        return set;
    }, [hasAmazon, hasRakuten, hasYahoo]);

    useEffect(() => {
        if (availableMalls.has(mall)) return;
        const fallbackMall = mallOptions.find((option) => availableMalls.has(option));
        if (fallbackMall) setMall(fallbackMall);
    }, [availableMalls, mall, mallOptions, setMall]);

    useEffect(() => {
        const currentSection = pageToSection[page];
        if (!currentSection) return;
        setOpenSections((prev) => ({
            ...prev,
            [currentSection]: true,
        }));
    }, [page, pageToSection]);

    const movePage = (nextPage: PageId) => {
        setPage(nextPage);
        setSidebarOpen(false);
    };

    const toggleSection = (key: SectionKey) => {
        const next = { ...openSections, [key]: !openSections[key] };
        setOpenSections(next);
        // 明示的な開閉のみ永続化（ページ遷移による自動オープンは保存しない）
        try {
            localStorage.setItem(OPEN_SECTIONS_LS_KEY, JSON.stringify(next));
        } catch {
            // プライベートモード等で保存できなくても開閉自体は動作させる
        }
    };

    const openExternal = (url: string) => {
        setSidebarOpen(false);
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const handleContactClick = () => {
        if (isFreePlan) {
            openExternal("https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform?usp=header");
            return;
        }
        openExternal("https://docs.google.com/forms/d/e/1FAIpQLSdrzL9WveXl_qdF3CwmYk6rFgrLDBfjbItdyuvT3pwrdVRveQ/viewform");
    };

    const handleGuideClick = () => {
        openExternal("https://ec-vision.co.jp/profit-guide/");
    };

    const handleRequestClick = () => {
        openExternal("https://forms.gle/d63pmR99TfefLhSv7");
    };

    const handleLogout = async () => {
        if (isLoggingOut) return;

        try {
            setIsLoggingOut(true);
            setSidebarOpen(false);

            if (isDemo) {
                navigate("/demo/dashboard");
                return;
            }

            const sp = new URLSearchParams(window.location.search);
            const loginTenant = sessionStorage.getItem("login_tenant");
            const selectedTenant = localStorage.getItem("selectedTenant");
            const urlTenant = sp.get("tenantId");

            console.log("[Sidebar] logout: tenant sources", {
                loginTenant,
                selectedTenant,
                urlTenant,
                userTenantId: user.tenantId,
            });

            const tenantId = loginTenant || selectedTenant || urlTenant || user.tenantId || "";

            localStorage.removeItem("ecx_access_token");
            localStorage.removeItem("ecx_token_expiry");
            localStorage.removeItem("selectedTenant");

            localStorage.removeItem("companyId");
            localStorage.removeItem("selectedCompanyId");
            localStorage.removeItem("shopUrl");
            localStorage.removeItem("selectedShopUrl");

            localStorage.removeItem("plan");
            localStorage.removeItem("trialStatus");

            sessionStorage.removeItem("login_tenant");
            sessionStorage.removeItem("ecx_oauth_state");
            sessionStorage.removeItem("ecx_code_verifier");

            localStorage.removeItem("impTenantId");
            localStorage.removeItem("impCompanyId");
            localStorage.removeItem("impShopUrl");
            localStorage.removeItem("superImpersonateCompanyId");

            // ✅ logout 前に tenantId を保存して、削除後に logout() に渡す
            // user.tenantId を優先（最も確実）
            const logoutTenantId = user.tenantId || loginTenant || selectedTenant || urlTenant || "";
            console.log("[Sidebar] logout with tenantId", { logoutTenantId, userTenantId: user.tenantId });

            localStorage.removeItem("selectedTenant");
            await logout(logoutTenantId);

            if (tenantId === "ecx") {
                navigate("/ecx/login");
                return;
            }
            navigate("/login");
        } catch (e) {
            console.error("logout failed", e);
            alert("ログアウトに失敗しました。");
        } finally {
            setIsLoggingOut(false);
        }
    };

    const renderItem = ({
        label,
        icon,
        active,
        onClick,
        disabled = false,
        premium = false,
    }: {
        label: string;
        icon: React.ReactNode;
        active: boolean;
        onClick?: () => void;
        disabled?: boolean;
        premium?: boolean;
    }) => {
        const iconColor = active
            ? "var(--nav-accent)"
            : premium
                ? PREMIUM_RGB
                : "var(--nav-text-muted)";

        const labelColor = active
            ? "#0C3F7A"
            : "var(--nav-text)";

        return (
            <button
                type="button"
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
                className={cx(
                    "relative flex w-full items-center gap-[10px] px-4 py-[7px] pl-[54px] text-left transition-colors",
                    active && "bg-[var(--nav-accent-light)]",
                    !disabled && "cursor-pointer hover:bg-[var(--nav-hover)]",
                    disabled && "cursor-not-allowed opacity-50"
                )}
            >
                {active && (
                    <span className="absolute bottom-1 left-0 top-1 w-[3px] rounded-r-[2px] bg-[var(--nav-accent)]" />
                )}

                <span className="shrink-0" style={{ color: iconColor }}>
                    {icon}
                </span>

                <span
                    className={cx(
                        "whitespace-nowrap text-[13px]",
                        active && "font-medium"
                    )}
                    style={{ color: labelColor }}
                >
                    {label}
                </span>

                {disabled && (
                    <span className="ml-auto shrink-0 rounded-full bg-slate-100 px-1 py-[1px] text-[9px] font-medium text-slate-400">
                        準備中
                    </span>
                )}
            </button>
        );
    };

    const renderSection = ({
        sectionClassName,
        label,
        headerIcon,
        headerOpen,
        onToggle,
        children,
        premium = false,
    }: {
        sectionClassName: string;
        label: string;
        headerIcon: React.ReactNode;
        headerOpen: boolean;
        onToggle: () => void;
        children: React.ReactNode;
        premium?: boolean;
    }) => (
        <div className={cx("nav-section", sectionClassName)}>
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full cursor-pointer select-none items-center gap-[10px] px-4 py-[9px] text-left transition-colors hover:bg-[var(--nav-hover)]"
            >
                <span className="section-icon-wrap flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px]">
                    {headerIcon}
                </span>

                <span
                    className="flex-1 text-[12px] font-medium tracking-[0.3px]"
                    style={{
                        color: premium ? PREMIUM_RGB : "var(--nav-text-muted)",
                    }}
                >
                    {label}
                </span>

                <ChevronIcon closed={!headerOpen} />
            </button>

            <div
                className={cx(
                    "overflow-hidden transition-[max-height] duration-200 ease-out",
                    !headerOpen && "max-h-0"
                )}
                style={{ maxHeight: headerOpen ? 480 : undefined }}
            >
                {children}
            </div>
        </div>
    );

    return (
        <div
            className="flex h-full flex-col font-[var(--font-sans)]"
            style={
                {
                    ["--nav-bg" as any]: "var(--color-background-primary)",
                    ["--nav-border" as any]: "var(--color-border-tertiary)",
                    ["--nav-accent" as any]: "#1E5FA8",
                    ["--nav-accent-light" as any]: "#E6F1FB",
                    ["--nav-accent-dark" as any]: "#0C3F7A",
                    ["--nav-text" as any]: "var(--color-text-primary)",
                    ["--nav-text-muted" as any]: "var(--color-text-secondary)",
                    ["--nav-hover" as any]: "var(--color-background-secondary)",
                    ["--nav-premium" as any]: "#BA7517",
                    ["--nav-premium-light" as any]: "#FAEEDA",
                } as React.CSSProperties
            }
        >
            <div className="px-0 py-2">

                {/* ── モール切り替えセレクト ── */}
                <div className="mx-3 mb-2 mt-1">
                    <label className="mb-1 block px-1 text-[11px] font-medium text-[var(--nav-text-muted)]">
                        表示モール
                    </label>
                    <div className="relative">
                        <select
                            aria-label="表示モール"
                            value={mall}
                            onChange={(e) => setMall(e.target.value as Mall)}
                            className="w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2 pr-9 text-[13px] font-medium text-[var(--nav-text)] outline-none transition-colors hover:border-slate-300 focus:border-[#1E5FA8] focus:ring-2 focus:ring-[#E6F1FB]"
                        >
                            {mallOptions.map((option) => (
                                <option key={option} value={option} disabled={!availableMalls.has(option)}>
                                    {MALL_LABELS[option]}
                                </option>
                            ))}
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-[var(--nav-text-muted)]">
                            <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
                                <path
                                    d="M4 6l4 4 4-4"
                                    stroke="currentColor"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </div>
                </div>

                {renderSection({
                    sectionClassName: "sec-goal",
                    label: "目標を立てる",
                    headerIcon: <SectionGoalIcon />,
                    headerOpen: openSections.goal,
                    onToggle: () => toggleSection("goal"),
                    children: (
                        <>
                            {renderItem({
                                label: "目標設定",
                                icon: <IconGoalFlag />,
                                active: page === "targetDashboard",
                                onClick: () => movePage("targetDashboard"),
                            })}
                        </>
                    ),
                })}

                {renderSection({
                    sectionClassName: "sec-profit",
                    label: "利益を確認する",
                    headerIcon: <SectionProfitIcon />,
                    headerOpen: openSections.profit,
                    onToggle: () => toggleSection("profit"),
                    children: (
                        <>
                            {renderItem({
                                label: "ダッシュボード",
                                icon: <IconDashboard />,
                                active: page === "dashboard",
                                onClick: () => movePage("dashboard"),
                            })}
                            {renderItem({
                                label: "月次利益",
                                icon: <IconMonthly />,
                                active: page === "monthly",
                                onClick: () => movePage("monthly"),
                            })}
                            {renderItem({
                                label: "日次利益",
                                icon: <IconDaily />,
                                active: page === "daily",
                                onClick: () => movePage("daily"),
                            })}
                            {renderItem({
                                label: "期間利益",
                                icon: <IconPeriod />,
                                active: page === "periodSnapshots",
                                onClick: () => movePage("periodSnapshots"),
                            })}
                            
                            {renderItem({
                                label: "商品別利益",
                                icon: <IconSingleProfit />,
                                active: page === "itemProfitSummary",
                                onClick: () => movePage("itemProfitSummary"),
                            })}
                            {/*}
                            {renderItem({
                                label: "商品別利益（詳細）",
                                icon: <IconSummaryProfit />,
                                active: page === "itemProfitDetail",
                                onClick: () => movePage("itemProfitDetail"),
                            })}
                            {renderItem({
                                label: "配送コスト設定",
                                icon: <IconCost />,
                                active: page === "shippingCostSettings",
                                onClick: () => movePage("shippingCostSettings"),
                            })}
                        　  */}
                        </>
                    ),
                })}

                {renderSection({
                    sectionClassName: "sec-analysis",
                    label: "分析をする",
                    headerIcon: <SectionAnalysisIcon />,
                    headerOpen: openSections.analysis,
                    onToggle: () => toggleSection("analysis"),
                    children: (
                        <>
                            {/* マクロ→ミクロのドリルダウン順に上から並べる（RN-161） */}
                            {renderItem({
                                label: "カテゴリー",
                                icon: <IconCategory />,
                                active: page === "categoryAnalysis",
                                onClick: () => movePage("categoryAnalysis"),
                            })}
                            {renderItem({
                                label: "商品名",
                                icon: <IconProductName />,
                                active: page === "productNameAnalysis",
                                onClick: () => movePage("productNameAnalysis"),
                            })}
                            {renderItem({
                                label: "商品分析",
                                icon: <IconProductAnalysis />,
                                active: page === "productAnalysis",
                                onClick: () => movePage("productAnalysis"),
                            })}
                            {/*
                            準備中の分析メニュー（仕様シート「変動費入力」①の構成に合わせる）
                            リリースまで一旦非表示
                            {[
                                { label: "RPP広告分析", icon: <IconAds /> },
                                { label: "LTV分析", icon: <IconRepeat /> },
                                { label: "参照元分析", icon: <IconPromotion /> },
                                { label: "月間検索ボリューム", icon: <IconAI /> },
                                { label: "新規・リピート", icon: <IconUsers /> },
                                { label: "稼働率", icon: <IconPeriod /> },
                                { label: "在庫回転", icon: <IconSummaryProfit /> },
                            ].map((item) => (
                                <React.Fragment key={item.label}>
                                    {renderItem({
                                        label: item.label,
                                        icon: item.icon,
                                        active: false,
                                        onClick: () => {},
                                        disabled: true,
                                    })}
                                </React.Fragment>
                            ))}
                            */}
                        </>
                    ),
                })}
                {renderSection({
                    sectionClassName: "sec-settings",
                    label: "設定を行う",
                    headerIcon: <SectionSettingsIcon />,
                    headerOpen: openSections.settings,
                    onToggle: () => toggleSection("settings"),
                    children: (
                        <>
                            {renderItem({
                                label: "初期設定",
                                icon: <IconInitialSettings />,
                                active: page === "settings",
                                onClick: () => movePage("settings"),
                            })}
                            {/* 費用の入力メニューはP/Lの費用構造（原価→変動費→固定費）の順に並べる */}
                            {renderItem({
                                label: "商品原価",
                                icon: <IconCost />,
                                active: page === "itemCost",
                                onClick: () => movePage("itemCost"),
                            })}
                            {renderItem({
                                label: "変動費入力",
                                icon: <IconVariableCost />,
                                active: page === "variableCostSettings",
                                onClick: () => movePage("variableCostSettings"),
                            })}
                            {renderItem({
                                label: "固定費入力",
                                icon: <IconFixedCost />,
                                active: page === "fiscalRakutenExpense",
                                onClick: () => movePage("fiscalRakutenExpense"),
                            })}
                            {renderItem({
                                label: "Amazon連携設定",
                                icon: <IconAmazon />,
                                active: page === "amazonSettings",
                                onClick: () => movePage("amazonSettings"),
                            })}
                            {/* 管理ツールの「利用モール」でYahoo!が有効化された会社にのみ表示する */}
                            {enabledMalls.includes("yahoo") &&
                                renderItem({
                                    label: "Yahoo連携設定",
                                    icon: <IconYahoo />,
                                    active: page === "yahooSettings",
                                    onClick: () => movePage("yahooSettings"),
                                })}
                            {renderItem({
                                label: "プラン・お支払い",
                                icon: <IconPlanBilling />,
                                active: page === "planBilling",
                                onClick: () => movePage("planBilling"),
                            })}
                            {isAdmin &&
                                renderItem({
                                    label: "ユーザー管理",
                                    icon: <IconUsers />,
                                    active: page === "users",
                                    onClick: () => movePage("users"),
                                })}
                        </>
                    ),
                })}

                {/* コンポーネント B: 初期設定ガイド常設リンク */}
                <a
                    href={getGuideUrl(planForMenu, user.tenantId)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-[10px] px-4 py-[9px] text-[13px] font-bold transition-colors"
                    style={{ background: "#EEEDFE", color: "#3C3489", borderLeft: "3px solid #534AB7" }}
                >
                    <svg
                        viewBox="0 0 13 13"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        className="h-[13px] w-[13px] shrink-0"
                    >
                        <circle cx="6.5" cy="6.5" r="5" />
                        <line x1="6.5" y1="4" x2="6.5" y2="6.5" />
                        <circle cx="6.5" cy="9" r="0.35" fill="currentColor" />
                    </svg>
                    初期設定ガイド
                </a>

                <div className="mx-4 my-[6px] h-px bg-[var(--nav-border)]" />

                {renderSection({
                    sectionClassName: "sec-premium",
                    label: "有償サポート",
                    headerIcon: <SectionPremiumIcon />,
                    headerOpen: openSections.premium,
                    onToggle: () => toggleSection("premium"),
                    premium: true,
                    children: (
                        <>
                            {renderItem({
                                label: "過去月データ取得",
                                icon: <IconPastData />,
                                active: page === "rmsManual",
                                onClick: () => movePage("rmsManual"),
                                premium: true,
                            })}
                            {/*
                            準備中のためリリースまで一旦非表示
                            {renderItem({
                                label: "分析レポート",
                                icon: <IconReport />,
                                active: false,
                                disabled: true,
                                premium: true,
                            })}
                            {renderItem({
                                label: "コンサル",
                                icon: <IconConsult />,
                                active: false,
                                disabled: true,
                                premium: true,
                            })}
                            {renderItem({
                                label: "制作・撮影",
                                icon: <IconCreative />,
                                active: false,
                                disabled: true,
                                premium: true,
                            })}
                            */}
                        </>
                    ),
                })}

                {renderSection({
                    sectionClassName: "sec-support",
                    label: "お問い合わせ",
                    headerIcon: <SectionSupportIcon />,
                    headerOpen: openSections.support,
                    onToggle: () => toggleSection("support"),
                    children: (
                        <>
                            {renderItem({
                                label: "お問い合わせ",
                                icon: <IconContact />,
                                active: false,
                                onClick: handleContactClick,
                            })}
                            {/*renderItem({
                                label: "改善要望",
                                icon: <IconRequest />,
                                active: false,
                                onClick: handleRequestClick,
                            })
                            {renderItem({
                                label: "ユーザーガイド",
                                icon: <IconGuide />,
                                active: false,
                                onClick: handleGuideClick,
                            })}
                            */}
                        </>
                    ),
                })}
            </div>

            <div className="px-0 pb-4 pt-2">
                <button
                    type="button"
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={cx(
                        "flex w-full items-center gap-[10px] px-4 py-2 text-left transition-colors hover:bg-[var(--nav-hover)]",
                        isLoggingOut && "cursor-not-allowed opacity-60"
                    )}
                >
                    <span className="shrink-0 text-[#A32D2D]">
                        <IconLogout />
                    </span>
                    <span className="text-[13px] text-[#A32D2D]">
                        {isLoggingOut ? "ログアウト中..." : "ログアウト"}
                    </span>
                </button>
            </div>

            <style>{`
                .sec-goal .section-icon-wrap { background: #E6F1FB; }
                .sec-profit .section-icon-wrap { background: #E6F1FB; }
                .sec-analysis .section-icon-wrap { background: #EEF3F8; }
                .sec-settings .section-icon-wrap { background: #F1EFE8; }
                .sec-premium .section-icon-wrap { background: #FAEEDA; }
                .sec-support .section-icon-wrap { background: #EEEDFE; }

                .sec-goal .section-icon-wrap svg { color: #1E5FA8; }
                .sec-profit .section-icon-wrap svg { color: #1E5FA8; }
                .sec-analysis .section-icon-wrap svg { color: #3A7BBF; }
                .sec-settings .section-icon-wrap svg { color: #5F5E5A; }
                .sec-premium .section-icon-wrap svg { color: rgb(186, 117, 23); }
                .sec-support .section-icon-wrap svg { color: #534AB7; }
            `}</style>
        </div>
    );
};

export default Sidebar;